import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BottomNavigation,
  Button,
  Controller,
  Delimiter,
  FormDirtyModal,
  Input,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import {
  CreateLmsCertificateDto,
  LmsCertificate,
  UpdateLmsCertificateDto,
} from 'src/graphql/generated/graphql';
import { DocumentNode } from '@apollo/client';
import { useLmsCertificateCreate } from 'src/hooks/query/lms/certificates/use-certificate-create';
import { useLmsCertificateUpdate } from 'src/hooks/query/lms/certificates/use-certificate-update';
import { Form } from './types';
import {
  mapFormDataToLmsCertificateDto,
  mapLmsCertificateToFormData,
} from './utils';
import styles from './index.module.css';
import { ProgramsField } from 'src/components/lms/curriculums/form/fields/programs';

type LmsCertificateFormProps = {
  onFinish?: () => void;
  onClose?: () => void;
  entity?: LmsCertificate;
  refetchQueries?: DocumentNode[];
  title?: React.ReactNode | string;
};

export const LmsCertificateForm = ({
  entity,
  onFinish,
  refetchQueries,
  onClose,
  title,
}: LmsCertificateFormProps) => {
  const { t } = useTranslation();
  const [create, { loading: loadingCreate }] =
    useLmsCertificateCreate(refetchQueries);
  const [update, { loading: loadingUpdate }] =
    useLmsCertificateUpdate(refetchQueries);
  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const loading = loadingCreate || loadingUpdate;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<Form>();

  useEffect(() => {
    if (!entity) {
      return;
    }
    const formData = mapLmsCertificateToFormData(entity as any);
    reset(formData);
  }, [entity, reset]);

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const onSubmit = async (data: Form) => {
    const entityToUpdate = mapFormDataToLmsCertificateDto({
      form: data,
    });
    if (entity?.id) {
      await update({
        variables: {
          id: entity.id,
          data: entityToUpdate as UpdateLmsCertificateDto,
        },
      });
    } else {
      await create({
        variables: {
          data: entityToUpdate as CreateLmsCertificateDto,
        },
      });
    }

    onFinish?.();
  };

  const titleElement =
    title || (t('lms.certificates.form.create-certificate') as string);

  return (
    <PageModal open onClose={onCancel} title={titleElement}>
      <div className={styles.wrapper}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.fieldsWrapper}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('lms.certificates.form.fields.name')}
                  placeholder={t('lms.certificates.form.fields.name')}
                  error={!!errors.name}
                  required={true}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.description ? 'red' : ''}
                  >
                    {t('lms.certificates.form.fields.description')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
            <Controller
              name="programId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    variant="subtitle1"
                    color={!!errors.mode ? 'red' : ''}
                  >
                    {t('lms.certificates.form.fields.program')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <ProgramsField field={field} hasError={!!errors.programId} />
                </div>
              )}
            />
          </div>

          <Delimiter className="mt-6" />

          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loading}
            >
              {t('lms.certificates.form.submit-button')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>

      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onClose?.()}
      />
    </PageModal>
  );
};

export default LmsCertificateForm;
